// Vendor
import {
  CanceledError,
    type AxiosError,
    type AxiosProgressEvent,
    type RawAxiosRequestConfig,
    type ResponseType
} from "axios";

// Stores
import { useApiStore } from '@stores/api';

// Personal libs
import { ApiErrorLabel } from "@shared/enum/api-error-label.enum";
import { HttpStatusCode } from "@shared/enum/http-status-code.enum";
import { logger } from "@shared/lib/logger/logger-manager";
import { ApiError } from "@shared/error/api.error";
import { TypeofEnum } from "@shared/enum/typeof.enum";
import { IApiResponse } from "@shared/interface/api-response.interface";
import { ApiStatusEnum } from "@shared/enum/api-enum";
import { sessionManager } from '@shared/service/sessionManager/session-manager.class';
import endpoints from 'src/shared/service/endpoint/endpoints.class';

// ********************************************************************************************************** INTERFACES
export interface IRequestParams {
    urlParams?: string | number;
    addTokens?: boolean;
    publicApi?: boolean;
    bypassManageError?: boolean;
    withCredentials?: boolean;
    abortController?: AbortController;
}

export interface IDefaultAxiosConfig {
    signal?: AbortSignal;
    responseType?: ResponseType;
    withCredentials?: boolean;
}

// ********************************************************************************************************** CONSTANTS
export const baseURL = `${process.env.BACKEND.host ?? ''}${
    process.env.BACKEND.port ? ':' + process.env.BACKEND.port : ''
  }${process.env.BACKEND.context ?? ''}`;

export const apiWhiteList = ['auth', 'auth/refresh'];

export const defaultRequestParams: IRequestParams = {
    urlParams: '',
    addTokens: true,
    publicApi: false,
};

// ********************************************************************************************************** GENERICS
export const testApiValidity = async (isPublicApi?: boolean): Promise<boolean> => {
    const publicApi = isPublicApi ?? false;
  
    if (publicApi === false) {
        return await sessionManager.checkAccessTokenValidity();
    } else {
        return true;
    }
};

export const isUploadEndpoint = (apiEndpoint?: string): boolean => {
    const uploadEndpoints = [endpoints.video.postVideo.getUrl()];
    return apiEndpoint != null && uploadEndpoints.includes(apiEndpoint);
};

export const onUploadProgressTus = (bytesUploaded: number, bytesTotal: number) => {
    const apiStore = useApiStore();
    apiStore.uploadLoaded = bytesUploaded;
    apiStore.uploadTotal = bytesTotal;
};

export const onUploadProgressAxios = (progressEvent: AxiosProgressEvent) => {
  const apiStore = useApiStore();
  apiStore.uploadLoaded = progressEvent.loaded;
  apiStore.uploadTotal = progressEvent.total;
};

export const buildUrl = async (
    api: string,
    params?: IRequestParams,
): Promise<string> => {
    return (
        baseURL + api + (params?.urlParams ? '/' + params.urlParams.toString() : '')
    );
};

export const defaultAxiosConfig = (
    params: IDefaultAxiosConfig,
): RawAxiosRequestConfig<any> => {
    return {
        withCredentials: params.withCredentials ?? true,
        signal: params.signal,
        responseType: params.responseType ?? 'json',
    };
};

/**
 *
 * @param providedObject
 * @returns {FormData}
 */
export const preparePostData = (providedObject: any): FormData => {
  const typesToStringify = [TypeofEnum.OBJECT];
  const typeToAvoid = [
    TypeofEnum.FUNCTION,
    TypeofEnum.SYMBOL,
    TypeofEnum.UNDEFINED,
  ];

  const postData = new FormData();
  Object.keys(providedObject).forEach((key) => {
    if (Array.isArray(providedObject[key])) {
      postData.append(key, JSON.stringify(providedObject[key]));
    } else {
      const typeofObject = typeof providedObject[key] as TypeofEnum;

      if (typesToStringify.includes(typeofObject)) {
        // Case where the postData is a file
        if (isFile(providedObject[key])) {
          postData.append(key, providedObject[key]);
        } else {
          postData.append(key, JSON.stringify(providedObject[key]));
        }
      } else if (!typeToAvoid.includes(typeofObject)) {
        postData.append(key, providedObject[key]);
      }
    }
  });

  return postData;
};

export const hasFile = (providedObject: any): boolean => {
  return isObject(providedObject) && Object.keys(providedObject).some((key) => isObject(providedObject[key]) && isFile(providedObject[key]))
}

const isObject = (data?: any): boolean => data != null && typeof data === 'object'
const isFile = (data?: any): boolean => data != null && isObject(data) && 'name' in data && 'size' in data && 'type' in data

export const returnError = <T>(code: string): IApiResponse<T, T> => {
    return {
      returnCode: code,
      returnStatus: ApiStatusEnum.ERROR,
    };
}

// ********************************************************************************************************** ERROR MANAGER
export const manageError = (
    method: string,
    api: string,
    error: AxiosError<any, any>,
  ): any => {
    logger?.warn('[DEBUG] INSIDE manageError', error);
  
    if (error instanceof CanceledError || error.code === 'ERR_CANCELED') {
      throw new ApiError(ApiErrorLabel.CancelledError, ApiErrorLabel.CancelledError);
    }

    if (error.response) {
      logger?.error(
        `ERROR in _${method.toLowerCase()} (1) : `,
        error.message,
        api,
      );
  
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      if (error.response?.data.returnCode != null) {
        if (
          error.request.status &&
          error.response.status == HttpStatusCode.RequestTimeout
        ) {
          throw new Error(ApiErrorLabel.RequestTimeout);
        }
  
        // Special cases
        if (apiWhiteList.includes(api)) {
          return error.response.data;
        }
  
        return error.response.data;
      } else if (
        error.request.status &&
        error.response.status == HttpStatusCode.InternalServerError
      ) {
        throw new Error(ApiErrorLabel.InternalServerError);
      } else {
        return error.response;
      }
    } else if (error.request && !error.message) {
      // The request was made but no response was received
      // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
      // http.ClientRequest in node.js
      logger?.error(
        `ERROR in _${method.toLowerCase()} (2) : `,
        error.request,
        api,
      );
    } else if (error.message) {
      // Something happened in setting up the request that triggered an Error
      if (error.message === ApiErrorLabel.NetworkError) {
        throw new Error(ApiErrorLabel.NetworkError);
      } else if (error.message === ApiErrorLabel.LoggingOutError) {
        logger?.error(
          `LOGGING_OUT_ERROR in _${method.toLowerCase()} : `,
          error,
          api,
        );
        throw new Error(ApiErrorLabel.LoggingOutError);
      } else {
        logger?.error(`ERROR in _${method.toLowerCase()} (3) : `, error, api);
      }
    }
  };

/**
 *
 * @param {ErrorConstructor} err
 * @param {string} methodName
 */
export const manageApiError = (err: ErrorConstructor, methodName?: string) => {
    if (err instanceof ApiError) {
        if (methodName != null) {
            logger?.warn(`${methodName} - err : ${err.code}`);
        }
  
      throw err;
    } else {
        if (methodName != null) {
            logger?.warn(`${methodName} - err : ${err.toString()}`);
        }

        throw new Error(err.toString());
    }
};

// ********************************************************************************************************** STORE
/**
 * Remove cancelToken
 * @param {AbortController} abortController
 */
export const removeAbortController = (abortController: AbortController) => {
  // Stores
  const apiStore = useApiStore();
  apiStore.removeAbortController(abortController);
};

/**
 * Add cancelToken
 * @param {AbortController} abortSignal
 */
export const addAbortController = (abortController: AbortController) => {
  // Stores
  const apiStore = useApiStore();
  apiStore.addAbortController(abortController);
};